import { useForm, FormProvider } from 'react-hook-form';
import { Stack, Box } from '@mui/system';
import { Divider } from '@mui/material';
import { mapCountry, EnumCountry } from 'tdc-web-backend/enums/enums';
import { CompanyDto } from 'tdc-web-backend/companies/schemas';
import { useTranslation } from 'react-i18next';
import CustomButton from '../../../../../../../../../../components/button/CustomButton';
import { ReactComponent as PlusIcon } from '../../../../../../../../../../assets/icons/button-icons/PlusIconCompanyEditModal.svg';
import { ReactComponent as GeoLoc } from '../../../../../../../../../../assets/icons/geo-alt-fill.svg';
import CustomChip from '../../../../../../../../../../components/custom-chip/CustomChip';
import ConfirmModal from '../../../../../../../../../../components/confirm-modal/ConfirmModal';
import CustomInput from '../../../../../../../../../../components/custom-input/CustomInput';
import { primaryLight } from '../../../../../../../../../../utils/color';
import { useRefresh } from '../../../../../../../../../../utils/hooks/crud-hooks/useRefresh';
import { mapStates } from '../../../../../../../../../../utils/enums';
import useUserCompany from '../../../../../../../../../../utils/hooks/useUserCompany';

interface EditCompanyOverViewProps {
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  showCancelButton: boolean;
}

interface FormValues extends CompanyDto {
  tempCountry: EnumCountry;
  tempCity: string;
}

const CompanyOfficeLocationsEditModal = ({
  isModalOpen,
  setIsModalOpen,
  showCancelButton,
}: EditCompanyOverViewProps) => {
  const refresh = useRefresh();
  const { company: companyData, updateCompany, isLoadingUpdateCompany } = useUserCompany();
  const methods = useForm<FormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      officeLocations: companyData?.officeLocations || [],
      tempCountry: '' as EnumCountry,
      tempCity: '',
    },
  });
  const { t } = useTranslation();
  const { setValue, watch } = methods;

  const officeLocations = watch('officeLocations') || [];
  const tempCountryValue = watch('tempCountry');
  const tempCityValue = watch('tempCity');

  const handleAddOffice = () => {
    const { tempCountry, tempCity } = methods.getValues();

    const locationAlreadyExists = officeLocations.some(
      (location) => location.country === tempCountry && location.city === tempCity,
    );

    if (locationAlreadyExists) {
      methods.setError('tempCity', { type: 'manual', message: 'Location already exists' });
      return;
    }

    const newOfficeLocation = { country: tempCountry, city: tempCity };
    const updatedLocations = [...officeLocations, newOfficeLocation];
    setValue('officeLocations', updatedLocations);
    setValue('tempCountry', '' as EnumCountry);
    setValue('tempCity', '');
    methods.clearErrors('officeLocations');
  };

  const handleRemoveOffice = (index: number) => {
    const updatedLocations = officeLocations.filter((_, i) => i !== index);
    setValue('officeLocations', updatedLocations);
  };

  const onSubmit = () => {
    const officeLocations = methods.getValues('officeLocations');

    updateCompany(
      {
        officeLocations,
      },
      {
        onSuccess: () => {
          refresh();
          setIsModalOpen(false);
        },
      },
    );
  };

  return (
    <ConfirmModal
      isModalOpen={isModalOpen}
      setIsModalOpen={setIsModalOpen}
      confirmButtonText={t('common.save')}
      onCancel={() => setIsModalOpen(false)}
      onConfirm={() => onSubmit()}
      automaticallyCloseModalOnButtonClick={false}
      isLoading={isLoadingUpdateCompany}
      title={t('settings.companyProfile.additionalOfficeLocations')}
      width="40%"
      iconProps={{
        icon: <GeoLoc fill={primaryLight[500]} />,
        wrapperBackgroundColor: '#E3DBFC',
      }}
      message={
        <form>
          <FormProvider {...methods}>
            <Stack spacing={2} direction="row" alignItems="center" justifyContent="flex-start" />

            <Stack
              sx={{
                height: 'auto',
                maxHeight: '400px',
                overflowY: 'auto',
                fontSize: '12px',
                fontFamily: 'Gellix-SemiBold',
              }}
              spacing={2}
              pr="10px"
            >
              <Stack spacing={2}>
                <CustomInput
                  required
                  input={<CustomInput.Autocomplete />}
                  name="tempCountry"
                  choices={mapCountry}
                  translation={{ t, ns: 'enum' }}
                  label={t('common.country')}
                  placeholder={t('common.select')}
                  sx={{ width: '100%', minHeight: '100%' }}
                />
                <CustomInput
                  required
                  input={<CustomInput.Text />}
                  name="tempCity"
                  label={t('common.city')}
                  placeholder={t('common.type')}
                  sx={{ width: '100%', minHeight: '100%' }}
                />
              </Stack>

              <Box style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                <CustomButton
                  variant="primaryLight"
                  sx={{ width: 'auto', mt: '10px', boxShadow: 'none' }}
                  onClick={handleAddOffice}
                  disabled={!tempCountryValue || !tempCityValue}
                  startIcon={<PlusIcon />}
                >
                  {t('settings.companyProfile.addOffice')}
                </CustomButton>
              </Box>

              <Divider />
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {officeLocations?.map((location, index) => (
                  <CustomChip
                    label={
                      location.state
                        ? `${location.city}, ${location.state}, ${
                            location.country ? mapCountry.get(location.country) : ''
                          }`
                        : `${location.city}, ${
                            location.country ? mapCountry.get(location.country) : ''
                          }`
                    }
                    sx={{
                      width: 'fit-content',
                      textTransform: 'capitalize',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    onDelete={() => handleRemoveOffice(index)}
                  />
                ))}
              </Stack>
            </Stack>
          </FormProvider>
        </form>
      }
      showCancelButton={showCancelButton}
    />
  );
};
export default CompanyOfficeLocationsEditModal;
